import { useState } from 'react'
import { useTranslation } from '@/contexts/TranslationContext'
import { Modal } from '@/components/ui'

interface Props {
  isOpen: boolean
  onClose: () => void
  party: {
    name: string
    whatsapp: string
  } | null
  message: string
  onConfigure: () => void
  onResetTemplate: () => void
  onSend: () => void
}

export function QuickChargeModal({
  isOpen,
  onClose,
  party,
  message,
  onConfigure,
  onResetTemplate,
  onSend,
}: Props) {
  const [showMenu, setShowMenu] = useState(false)

  if (!party) return null
  const { t } = useTranslation()
  const tc = (key: string, params?: Record<string, string>) => t(`billing.contacts.${key}`, params)
  const handleConfigure = () => {
    onConfigure()
    setShowMenu(false)
  }

  const handleResetTemplate = () => {
    onResetTemplate()
    setShowMenu(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={tc('quickChargeModal.title')}
      size="md"
      headerRight={
        <div className="flex items-center gap-2 relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <svg className="h-5 w-5 text-slate-600 dark:text-slate-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
          </button>

          <button
            onClick={onClose}
            aria-label={t('actions.close')}
            className="hidden md:inline-flex p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <svg className="h-5 w-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Dropdown menu */}
          {showMenu && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-50">
              <button
                onClick={handleConfigure}
                className="w-full text-left px-4 py-3 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 border-b border-slate-200 dark:border-slate-700 first:rounded-t-lg"
              >
                {tc('quickChargeModal.configure')}
              </button>
              <button
                onClick={handleResetTemplate}
                className="w-full text-left px-4 py-3 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 last:rounded-b-lg"
              >
                {tc('quickChargeModal.restoreDefault')}
              </button>
            </div>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {/* Party info */}
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-800/50">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{party.name}</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 font-mono">{party.whatsapp}</p>
        </div>

        {/* Message preview */}
        <div>
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
            {tc('quickChargeModal.messageLabel')}
          </p>
          <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words leading-relaxed">
              {message}
            </p>
          </div>
        </div>
        {/* Single send button below message (small, right-aligned) with WhatsApp icon */}
        <div className="flex justify-end">
          <button
            onClick={() => onSend()}
            title={tc('quickChargeModal.send')}
            className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors text-sm"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
              <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.975-1.395A9.959 9.959 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.948 7.948 0 01-4.048-1.107l-.29-.172-2.953.828.815-2.876-.188-.296A8 8 0 1112 20z" />
            </svg>
            <span>{tc('quickChargeModal.send')}</span>
          </button>
        </div>
      </div>
    </Modal>
  )
}
